import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../../services/api'
import toast from 'react-hot-toast'

export default function SignatureCopy() {
  const navigate = useNavigate()
  const [mode, setMode] = useState('job') // 'job' or 'ref'
  const [jobNumber, setJobNumber] = useState('')
  const [timesheets, setTimesheets] = useState([])
  const [sourceId, setSourceId] = useState('')
  const [destIds, setDestIds] = useState([])
  const [sourceRef, setSourceRef] = useState('')
  const [destRefs, setDestRefs] = useState('')
  const [status, setStatus] = useState('')
  const [loading, setLoading] = useState(false)
  const [looked, setLooked] = useState(false)
  const [checked, setChecked] = useState(false)

  // Job Mode - Lookup timesheets
  async function handleLookup() {
    if (!jobNumber.trim()) {
      toast.error('Please enter a Job Number')
      return
    }

    setLoading(true)
    try {
      const response = await api.get(`/api/timesheets/by-job/${encodeURIComponent(jobNumber.trim())}`)
      
      if (response.success && response.timesheets?.length > 0) {
        setTimesheets(response.timesheets)
        setLooked(true)
        
        const withSig = response.timesheets.filter(t => t.signature).length
        const withoutSig = response.timesheets.length - withSig
        setStatus(`Found ${response.timesheets.length} timesheet(s) for Job #${jobNumber}\n` +
          `  - With signature: ${withSig}\n` +
          `  - Without signature: ${withoutSig}\n\n` +
          `Select ONE source and one or more destinations, then click Copy.`)
      } else {
        setStatus(`No timesheets found for Job Number: ${jobNumber}`)
        setTimesheets([])
      }
    } catch (error) {
      toast.error('Failed to lookup timesheets')
      setStatus('Error looking up timesheets')
    }
    setLoading(false)
  }

  // Job Mode - Copy signature
  async function handleCopyJob() {
    if (!sourceId) {
      toast.error('Please select a SOURCE timesheet')
      return
    }
    if (destIds.length === 0) {
      toast.error('Please select at least one DESTINATION timesheet')
      return
    }

    setLoading(true)
    try {
      const response = await api.post('/api/timesheets/copy-signature', {
        sourceId,
        destinationIds: destIds
      })

      if (response.success) {
        const successCount = response.results.filter(r => r.success).length
        const failCount = response.results.length - successCount
        
        let statusText = '═══════════════════════════════════════\n'
        statusText += '           COPY COMPLETE\n'
        statusText += '═══════════════════════════════════════\n\n'
        
        const sourceTs = timesheets.find(t => t.id.toString() === sourceId || t.uuid === sourceId)
        if (sourceTs) {
          statusText += `Source: ${sourceTs.referenceNo} (${sourceTs.employeeName})\n`
          statusText += `Job #: ${sourceTs.workOrderNumber}\n\n`
        }
        
        statusText += `Successful: ${successCount} | Failed: ${failCount}\n\n`
        statusText += 'RESULTS:\n'
        
        for (const r of response.results) {
          statusText += r.success 
            ? `  ✓ ${r.referenceNo} - Success\n`
            : `  ✗ ${r.referenceNo} - ${r.message}\n`
        }
        
        statusText += '\nClick Reset to copy more signatures.'
        setStatus(statusText)
        setTimesheets([])
        setLooked(false)
        toast.success(`Copied signature to ${successCount} timesheet(s)`)
      }
    } catch (error) {
      toast.error('Failed to copy signature')
    }
    setLoading(false)
  }

  // Ref Mode - Check references
  async function handleCheckRef() {
    if (!sourceRef.trim()) {
      toast.error('Please enter a Source Reference Number')
      return
    }
    if (!destRefs.trim()) {
      toast.error('Please enter at least one Destination Reference Number')
      return
    }

    setLoading(true)
    try {
      // Check source
      const sourceResponse = await api.get(`/api/timesheets/by-reference/${encodeURIComponent(sourceRef.trim())}`)
      
      if (!sourceResponse.success || !sourceResponse.timesheet) {
        setStatus(`Source timesheet not found: ${sourceRef.trim()}`)
        setLoading(false)
        return
      }

      if (!sourceResponse.timesheet.signature) {
        setStatus(`Source timesheet (${sourceResponse.timesheet.referenceNo}) has no signature to copy.`)
        setLoading(false)
        return
      }

      // Parse and check destinations
      const destList = destRefs.split(',').map(r => r.trim()).filter(r => r)
      const destTimesheets = []
      const notFound = []

      for (const ref of destList) {
        try {
          const resp = await api.get(`/api/timesheets/by-reference/${encodeURIComponent(ref)}`)
          if (resp.success && resp.timesheet) {
            destTimesheets.push(resp.timesheet)
          } else {
            notFound.push(ref)
          }
        } catch {
          notFound.push(ref)
        }
      }

      if (notFound.length > 0) {
        setStatus(`The following were not found: ${notFound.join(', ')}`)
        setLoading(false)
        return
      }

      // Display confirmation
      let statusText = 'Ready to copy signature from:\n\n'
      statusText += 'SOURCE:\n'
      statusText += `  ${sourceResponse.timesheet.referenceNo} - Job #${sourceResponse.timesheet.workOrderNumber} - ${sourceResponse.timesheet.employeeName}\n\n`
      statusText += `TO ${destTimesheets.length} DESTINATION(S):\n`

      for (const dest of destTimesheets) {
        const hasSig = dest.signature ? ' [HAS SIGNATURE]' : ''
        statusText += `  • ${dest.referenceNo} - Job #${dest.workOrderNumber} - ${dest.employeeName}${hasSig}\n`
      }

      statusText += "\nClick 'Copy Signature' to proceed."
      setStatus(statusText)
      setChecked(true)
    } catch (error) {
      toast.error('Failed to check references')
    }
    setLoading(false)
  }

  // Ref Mode - Copy signature
  async function handleCopyRef() {
    const destList = destRefs.split(',').map(r => r.trim()).filter(r => r)
    
    setLoading(true)
    try {
      const response = await api.post('/api/timesheets/copy-signature', {
        sourceId: sourceRef.trim(),
        destinationIds: destList
      })

      if (response.success) {
        const successCount = response.results.filter(r => r.success).length
        const failCount = response.results.length - successCount
        
        let statusText = '═══════════════════════════════════════\n'
        statusText += '           COPY COMPLETE\n'
        statusText += '═══════════════════════════════════════\n\n'
        statusText += `Source: ${sourceRef}\n\n`
        statusText += `Successful: ${successCount} | Failed: ${failCount}\n\n`
        statusText += 'RESULTS:\n'
        
        for (const r of response.results) {
          statusText += r.success 
            ? `  ✓ ${r.referenceNo} - Success\n`
            : `  ✗ ${r.referenceNo} - ${r.message}\n`
        }
        
        statusText += '\nClick Reset to copy more signatures.'
        setStatus(statusText)
        setChecked(false)
        toast.success(`Copied signature to ${successCount} timesheet(s)`)
      }
    } catch (error) {
      toast.error('Failed to copy signature')
    }
    setLoading(false)
  }

  // Reset
  function handleReset() {
    setJobNumber('')
    setTimesheets([])
    setSourceId('')
    setDestIds([])
    setSourceRef('')
    setDestRefs('')
    setStatus('')
    setLooked(false)
    setChecked(false)
  }

  // Toggle destination checkbox
  function toggleDest(id) {
    setDestIds(prev => 
      prev.includes(id) 
        ? prev.filter(d => d !== id)
        : [...prev, id]
    )
  }

  // Select all destinations
  function toggleAllDest(checked) {
    if (checked) {
      const noSigIds = timesheets.filter(t => !t.signature).map(t => t.id.toString())
      setDestIds(noSigIds)
    } else {
      setDestIds([])
    }
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Signature Copy</h1>
        <p className="text-blue-100">Copy customer signatures between timesheets</p>
      </div>

      <div className="bg-white rounded-xl shadow-md p-6">
        {/* Mode Selection */}
        <div className="mb-6">
          <label className="form-label">Select Mode</label>
          <div className="flex gap-4 mt-2">
            <label className={`flex items-center gap-2 px-4 py-3 border-2 rounded-lg cursor-pointer ${mode === 'job' ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`}>
              <input
                type="radio"
                name="mode"
                checked={mode === 'job'}
                onChange={() => { setMode('job'); handleReset(); }}
                className="w-4 h-4"
              />
              <span className="font-medium">By Job Number</span>
            </label>
            <label className={`flex items-center gap-2 px-4 py-3 border-2 rounded-lg cursor-pointer ${mode === 'ref' ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`}>
              <input
                type="radio"
                name="mode"
                checked={mode === 'ref'}
                onChange={() => { setMode('ref'); handleReset(); }}
                className="w-4 h-4"
              />
              <span className="font-medium">By Reference Number</span>
            </label>
          </div>
        </div>

        {/* Job Number Mode */}
        {mode === 'job' && (
          <div>
            <div className="mb-4">
              <label className="form-label">Job Number (Work Order Number)</label>
              <input
                type="text"
                value={jobNumber}
                onChange={(e) => setJobNumber(e.target.value)}
                disabled={looked}
                placeholder="e.g., 26047J"
                className="mt-1"
              />
            </div>
            
            {!looked && (
              <button
                onClick={handleLookup}
                disabled={loading}
                className="btn btn-primary"
              >
                {loading ? 'Looking up...' : 'Lookup Timesheets'}
              </button>
            )}

            {/* Timesheets Table */}
            {timesheets.length > 0 && (
              <div className="mt-6">
                <div className="bg-blue-50 p-3 rounded-lg mb-4 text-sm">
                  <strong>Source:</strong> Select ONE timesheet that HAS a signature (radio button)<br/>
                  <strong>Destinations:</strong> Select timesheets that NEED the signature (checkboxes)
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full border">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-3 py-2 text-center border">Source</th>
                        <th className="px-3 py-2 text-center border">
                          <input
                            type="checkbox"
                            onChange={(e) => toggleAllDest(e.target.checked)}
                            title="Select All"
                          />
                        </th>
                        <th className="px-3 py-2 text-left border">Reference #</th>
                        <th className="px-3 py-2 text-left border">Employee</th>
                        <th className="px-3 py-2 text-left border">Date</th>
                        <th className="px-3 py-2 text-left border">Customer</th>
                        <th className="px-3 py-2 text-center border">Signature</th>
                      </tr>
                    </thead>
                    <tbody>
                      {timesheets.map(ts => {
                        const hasSig = !!ts.signature
                        return (
                          <tr key={ts.id} className="hover:bg-gray-50">
                            <td className="px-3 py-2 text-center border">
                              {hasSig && (
                                <input
                                  type="radio"
                                  name="sourceId"
                                  value={ts.id}
                                  checked={sourceId === ts.id.toString()}
                                  onChange={() => setSourceId(ts.id.toString())}
                                />
                              )}
                            </td>
                            <td className="px-3 py-2 text-center border">
                              {!hasSig && (
                                <input
                                  type="checkbox"
                                  checked={destIds.includes(ts.id.toString())}
                                  onChange={() => toggleDest(ts.id.toString())}
                                />
                              )}
                            </td>
                            <td className="px-3 py-2 border font-mono">{ts.referenceNo}</td>
                            <td className="px-3 py-2 border">{ts.employeeName}</td>
                            <td className="px-3 py-2 border">{ts.month || '-'}</td>
                            <td className="px-3 py-2 border">{ts.customer}</td>
                            <td className="px-3 py-2 text-center border">
                              {hasSig ? (
                                <span className="text-green-600 font-semibold">✓ Yes</span>
                              ) : (
                                <span className="text-red-500">✗ No</span>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="mt-4">
                  <button
                    onClick={handleCopyJob}
                    disabled={loading || !sourceId || destIds.length === 0}
                    className="btn btn-success"
                  >
                    {loading ? 'Copying...' : 'Copy Signature'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Reference Number Mode */}
        {mode === 'ref' && (
          <div>
            <div className="mb-4">
              <label className="form-label">Source Timesheet (has signature)</label>
              <input
                type="text"
                value={sourceRef}
                onChange={(e) => setSourceRef(e.target.value)}
                disabled={checked}
                placeholder="e.g., JDI0001"
                className="mt-1"
              />
            </div>

            <div className="mb-4">
              <label className="form-label">Destination Timesheets (need signature) - comma separated</label>
              <input
                type="text"
                value={destRefs}
                onChange={(e) => setDestRefs(e.target.value)}
                disabled={checked}
                placeholder="e.g., JDI0002, JDI0003, JDI0004"
                className="mt-1"
              />
            </div>

            <div className="flex gap-2">
              {!checked ? (
                <button
                  onClick={handleCheckRef}
                  disabled={loading}
                  className="btn btn-primary"
                >
                  {loading ? 'Checking...' : 'Check'}
                </button>
              ) : (
                <button
                  onClick={handleCopyRef}
                  disabled={loading}
                  className="btn btn-success"
                >
                  {loading ? 'Copying...' : 'Copy Signature'}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Common Buttons */}
        <div className="flex gap-2 mt-6 pt-4 border-t">
          <button onClick={handleReset} className="btn btn-danger">
            Reset
          </button>
          <button onClick={() => navigate(-1)} className="btn btn-secondary">
            Back
          </button>
        </div>

        {/* Status */}
        {status && (
          <div className="mt-6">
            <label className="form-label">Status</label>
            <pre className="mt-1 p-4 bg-gray-100 rounded-lg text-sm whitespace-pre-wrap font-mono">
              {status}
            </pre>
          </div>
        )}
      </div>
    </div>
  )
}